import { isLikelyMobile } from '../input/mobile';

export const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,16,0,0.6)',
  bgSolid: 'rgba(0,8,0,0.92)',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.5)',
} as const;

export const FONT = "'Share Tech Mono', monospace";
export const GLOW = `0 0 6px #00ff41, 0 0 12px rgba(0,255,65,0.3)`;
export const GLOW_STRONG = `0 0 10px #00ff41, 0 0 30px rgba(0,255,65,0.4), 0 0 60px rgba(0,255,65,0.15)`;

let keyframesInjected = false;

export function injectSharedKeyframes(): void {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes vs-title-glow {
      0%, 100% { text-shadow: ${GLOW_STRONG}; }
      50% { text-shadow: 0 0 20px #00ff41, 0 0 50px rgba(0,255,65,0.6), 0 0 80px rgba(0,255,65,0.25); }
    }
    @keyframes vs-pulse {
      0%, 100% { box-shadow: 0 0 8px #00ff41, 0 0 16px rgba(0,255,65,0.3); }
      50% { box-shadow: 0 0 16px #00ff41, 0 0 32px rgba(0,255,65,0.5), 0 0 48px rgba(0,255,65,0.2); }
    }
    @keyframes vs-star-drift {
      from { transform: translateY(0); }
      to { transform: translateY(-50%); }
    }
    @keyframes vs-scanline {
      0% { background-position: 0 0; }
      100% { background-position: 0 4px; }
    }
    @keyframes vs-fade-in {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    /* Create-pilot: stack preview + race grid on narrow viewports, cap preview height on phones. */
    .vs-char-create-content {
      box-sizing: border-box;
    }
    @media (max-width: 640px) {
      .vs-char-create-content {
        padding-left: max(12px, env(safe-area-inset-left, 0px)) !important;
        padding-right: max(12px, env(safe-area-inset-right, 0px)) !important;
        gap: 12px !important;
      }
      .vs-char-create-race-preview-canvas,
      .vs-char-create-ship-canvas {
        max-height: min(38vh, 220px) !important;
        aspect-ratio: 4 / 3;
      }
    }
    @keyframes vs-flicker {
      0%, 100% { opacity: 1; }
      92% { opacity: 1; }
      93% { opacity: 0.4; }
      94% { opacity: 1; }
    }

    /*
     * Compact-landscape overrides: only apply on viewports that look like
     * a phone in landscape (≤ 500px tall AND landscape orientation). This
     * lets the auth/menu screens fit on iPhone landscape without scrolling
     * while leaving desktop sizing completely unchanged. !important is
     * used here because the targeted elements are styled inline.
     */
    @media (max-height: 500px) and (orientation: landscape) {
      .vs-screen-title-large {
        font-size: clamp(20px, 8vh, 36px) !important;
        letter-spacing: 4px !important;
        white-space: nowrap !important;
      }
      .vs-screen-title-medium {
        font-size: clamp(16px, 6vh, 24px) !important;
        letter-spacing: 3px !important;
        white-space: nowrap !important;
      }
      .vs-screen-content {
        gap: 8px !important;
        padding: 8px !important;
      }
      .vs-screen-subtitle {
        margin-top: -4px !important;
      }
      .vs-screen-panel {
        padding: 12px clamp(16px, 4vw, 32px) !important;
        gap: 8px !important;
      }
      .vs-screen-form {
        gap: 6px !important;
      }

      /*
       * Char-select compaction. The Select Pilot grid renders a 4:3 ship
       * preview per card which alone eats >180px of vertical space — on a
       * 320–390px landscape phone that pushed the LAUNCH button below the
       * fold and forced the screen to scroll. We:
       *   - shrink the per-card preview to a 16:9 strip capped at ~96px,
       *   - tighten card padding/gaps,
       *   - hide the "last played: …" line (still shown on desktop),
       *   - shrink the launch button so it stays above the fold,
       *   - pin sign-out to the top-right as a small ghost button.
       */
      .vs-char-select-content {
        gap: 6px !important;
        padding: 6px 12px !important;
      }
      .vs-char-select-welcome {
        font-size: 9px !important;
        letter-spacing: 2px !important;
        opacity: 0.75;
      }
      .vs-char-select-grid {
        /*
         * auto-fit (vs auto-fill) collapses empty tracks so 1-3 cards
         * stay visually centered with the LAUNCH button below them
         * instead of getting pinned to the left of the grid.
         */
        grid-template-columns: repeat(auto-fit, minmax(160px, max-content)) !important;
        justify-content: center !important;
        gap: 8px !important;
        max-width: min(720px, 96vw) !important;
      }
      .vs-char-select-card {
        padding: 8px !important;
        gap: 4px !important;
      }
      .vs-char-select-preview-canvas {
        aspect-ratio: 16 / 9 !important;
        max-height: clamp(72px, 22vh, 110px) !important;
      }
      .vs-char-select-callsign {
        font-size: 13px !important;
        letter-spacing: 2px !important;
      }
      .vs-char-select-info,
      .vs-char-select-stats {
        font-size: 9px !important;
        letter-spacing: 1px !important;
      }
      .vs-char-select-lastplayed {
        display: none !important;
      }
      .vs-char-select-newcard {
        padding: 8px !important;
        min-height: 0 !important;
        gap: 4px !important;
      }
      .vs-char-select-newplus {
        font-size: 22px !important;
      }
      .vs-char-select-launch {
        font-size: 13px !important;
        padding: 8px 28px !important;
        letter-spacing: 3px !important;
      }
      .vs-char-select-signout {
        margin-top: 2px !important;
        padding: 4px 14px !important;
        font-size: 9px !important;
        letter-spacing: 1px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export function css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  Object.assign(el.style, styles);
}

export function el(
  tag: string,
  styles: Partial<CSSStyleDeclaration>,
  text?: string,
): HTMLElement {
  const e = document.createElement(tag);
  css(e, styles);
  if (text !== undefined) e.textContent = text;
  return e;
}

export function buildStarfield(root: HTMLElement): void {
  // position: fixed (not absolute) so the starfield stays anchored to the
  // viewport even when the screen-root above scrolls (e.g. on iOS landscape
  // where the auth/menu content can be taller than the safe-area height).
  const field = el('div', {
    position: 'fixed',
    inset: '0',
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: '0',
  });

  const stars = el('div', {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '200%',
    animation: 'vs-star-drift 60s linear infinite',
  });

  const count = 200;
  let shadow = '';
  for (let i = 0; i < count; i++) {
    const x = Math.random() * 100;
    const y = Math.random() * 100;
    const size = Math.random() < 0.1 ? 2 : 1;
    const opacity = 0.3 + Math.random() * 0.7;
    if (i > 0) shadow += ', ';
    shadow += `${x}vw ${y}vh 0 ${size > 1 ? '1px' : '0px'} rgba(0,255,65,${opacity})`;
  }
  const dot = el('div', {
    position: 'absolute',
    width: '1px',
    height: '1px',
    background: 'transparent',
    boxShadow: shadow,
  });
  stars.appendChild(dot);
  field.appendChild(stars);

  const scanline = el('div', {
    position: 'absolute',
    inset: '0',
    background:
      'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)',
    animation: 'vs-scanline 0.5s steps(2) infinite',
    pointerEvents: 'none',
  });
  field.appendChild(scanline);

  root.appendChild(field);
}

export function createScreenRoot(container: HTMLElement): HTMLElement {
  // The screen-root may be taller than the viewport on small landscape
  // devices (iPhone landscape after the Dynamic Island + home indicator
  // leaves only ~320px usable height). To avoid clipping the title at the
  // top and the action button at the bottom we:
  //
  //   1. Allow vertical scrolling (`overflow-y: auto`).
  //   2. Use `justify/align: safe center` so flex centering downgrades to
  //      `flex-start` whenever content overflows — without this, the top of
  //      the content would be pushed above the scroll origin and become
  //      unreachable.
  //   3. Apply `env(safe-area-inset-*)` padding so content never sits under
  //      the iOS notch / home indicator. Falls back to 0 elsewhere.
  const root = el('div', {
    position: 'absolute',
    inset: '0',
    display: 'none',
    flexDirection: 'column',
    alignItems: 'safe center',
    justifyContent: 'safe center',
    fontFamily: FONT,
    color: COLORS.primary,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    zIndex: '100',
    background: COLORS.bgSolid,
    overflowY: 'auto',
    overflowX: 'hidden',
    // Body sets touch-action: none to stop rubber-band on the game canvas,
    // but that also disables touch scrolling for our overlay screens. Re-
    // enable vertical pan + smooth iOS momentum scrolling here so users on
    // small landscape phones can reach overflowed buttons.
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingRight: 'env(safe-area-inset-right, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    paddingLeft: 'env(safe-area-inset-left, 0px)',
  });
  container.appendChild(root);
  return root;
}

export function createButton(
  label: string,
  onClick: () => void,
  variant: 'primary' | 'secondary' | 'ghost' = 'primary',
): HTMLButtonElement {
  const color = variant === 'secondary' ? COLORS.secondary : COLORS.primary;
  const btn = document.createElement('button');
  css(btn, {
    fontFamily: FONT,
    fontSize: variant === 'ghost' ? '11px' : 'clamp(13px, 2vw, 18px)',
    padding: variant === 'ghost' ? '8px 20px' : '12px 48px',
    background: 'transparent',
    border: variant === 'ghost'
      ? `1px solid ${COLORS.dim}`
      : `2px solid ${color}`,
    color,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: variant === 'ghost' ? '2px' : '4px',
    transition: 'all 0.2s',
    pointerEvents: 'auto',
  });
  if (variant !== 'ghost') {
    btn.style.animation = 'vs-pulse 2s ease-in-out infinite';
  }
  btn.textContent = label;

  btn.addEventListener('mouseenter', () => {
    btn.style.background = color;
    btn.style.color = '#000';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = 'transparent';
    btn.style.color = color;
  });
  btn.addEventListener('click', onClick);
  return btn;
}

export function createInput(
  placeholder: string,
  type = 'text',
  maxLength = 32,
): HTMLInputElement {
  const input = document.createElement('input');
  input.type = type;
  input.maxLength = maxLength;
  input.placeholder = placeholder;
  input.autocomplete = type === 'password' ? 'current-password' : 'username';
  css(input, {
    fontFamily: FONT,
    fontSize: '14px',
    padding: '10px 16px',
    background: 'rgba(0,8,0,0.8)',
    border: `1px solid ${COLORS.primary}`,
    boxShadow: `0 0 8px rgba(0,255,65,0.2), inset 0 0 8px rgba(0,255,65,0.05)`,
    color: COLORS.primary,
    textTransform: type === 'password' ? 'none' : 'uppercase',
    letterSpacing: '2px',
    outline: 'none',
    textAlign: 'center',
    width: 'clamp(220px, 30vw, 340px)',
    caretColor: COLORS.primary,
    pointerEvents: 'auto',
  });
  return input;
}

/**
 * Adds a small "✕" close button to the top-right corner of a panel/window,
 * but only on mobile devices. On desktop this is a no-op.
 *
 * The button is absolutely positioned inside `panel` (so `panel` should
 * be a positioning context — `position: absolute|relative|fixed`). The
 * provided `onClose` handler is invoked on tap; the button also stops
 * propagation so it doesn't bubble into the panel's own click handlers
 * (e.g. the controls-help root closes itself on any click).
 *
 * Mobile panels typically already carry a CANCEL/CLOSE text button in
 * their header, but that button can be small and awkward to hit on a
 * touch device; the corner ✕ gives players a predictable, always-at-the-
 * top-right way to dismiss any overlay.
 */
/**
 * Helper to check whether the mobile "✕" overlay is active. Kept here
 * (rather than importing `isLikelyMobile` directly from `../input/mobile`
 * at every call site) so panel code that already pulls from `ui-shared`
 * has a single place to ask "am I rendering in mobile layout?".
 */
export function isMobileLayout(): boolean {
  return isLikelyMobile();
}

export function attachMobileCloseButton(
  panel: HTMLElement,
  onClose: () => void,
  opts?: {
    /**
     * Existing in-header CANCEL / CLOSE / UNDOCK buttons that would
     * otherwise sit right next to the corner ✕ on mobile. Passing them
     * here hides them on mobile only, so we don't render two close
     * affordances on top of each other (the ✕ is much easier to hit on
     * a touch screen).
     */
    hideOnMobile?: HTMLElement[];
  },
): HTMLButtonElement | null {
  if (!isLikelyMobile()) return null;
  if (opts?.hideOnMobile) {
    for (const el of opts.hideOnMobile) {
      el.style.display = 'none';
    }
  }
  const btn = document.createElement('button');
  css(btn, {
    position: 'absolute',
    top: 'calc(6px + env(safe-area-inset-top, 0px))',
    right: 'calc(6px + env(safe-area-inset-right, 0px))',
    width: '36px',
    height: '36px',
    padding: '0',
    fontFamily: FONT,
    fontSize: '20px',
    lineHeight: '1',
    background: 'rgba(0,8,0,0.75)',
    border: `1px solid ${COLORS.primary}`,
    color: COLORS.primary,
    cursor: 'pointer',
    textShadow: GLOW,
    boxShadow: `0 0 8px rgba(0,255,65,0.25)`,
    pointerEvents: 'auto',
    // Keep the close button above the panel's own chrome (tabs, headers,
    // scanline overlays). 9999 is safely above the highest zIndex we
    // use inside panels.
    zIndex: '9999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'manipulation',
  });
  btn.textContent = '\u2715';
  btn.setAttribute('aria-label', 'Close');
  const stopAndClose = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };
  btn.addEventListener('click', stopAndClose);
  btn.addEventListener('touchend', stopAndClose);
  panel.appendChild(btn);

  // Make sure the panel reserves enough top-padding for the 36px
  // corner button to sit above any header / divider line. Floating
  // modals (friends-panel, mission-journal) ship with `paddingTop: 14px`,
  // which puts their borderBottom divider at ~y=40 — exactly where the
  // close button's bottom edge lands (top:6 + height:36 = 42). The
  // result was the user-reported "close button overlapping a line".
  //
  // Wrapping the existing paddingTop in `max()` means:
  //   - Panels with small fixed paddings get bumped up so the button
  //     clears the divider (48 = 6 top + 36 height + 6 gap).
  //   - Full-screen modals that already use `env(safe-area-inset-top)`
  //     keep their inset where it's larger (e.g. iPhone notches), and
  //     get the 48px floor on devices without a safe-area inset.
  const MIN_CLOSE_TOP_PAD = 48;
  const existingPadTop = panel.style.paddingTop || '0px';
  panel.style.paddingTop = `max(${MIN_CLOSE_TOP_PAD}px, ${existingPadTop})`;
  return btn;
}

export function createTitle(text: string, size: 'large' | 'medium' = 'large'): HTMLElement {
  // Desktop sizing kept identical to the original. The `vs-screen-title`
  // class is only used by the small-landscape media query in
  // injectSharedKeyframes to shrink the title on phones — desktop is
  // unaffected because the media query never matches there.
  const h = el('h1', {
    fontSize: size === 'large'
      ? 'clamp(32px, 6vw, 72px)'
      : 'clamp(20px, 3vw, 36px)',
    fontFamily: FONT,
    margin: '0',
    color: COLORS.primary,
    animation: 'vs-title-glow 3s ease-in-out infinite',
    letterSpacing: size === 'large' ? '8px' : '6px',
  }, text);
  h.classList.add('vs-screen-title', size === 'large' ? 'vs-screen-title-large' : 'vs-screen-title-medium');
  return h;
}
